export const CURRENT_TAX_YEAR = new Date().getFullYear();

export const getTaxYears = (
  startYear = CURRENT_TAX_YEAR,
  stopAtYear = 2017
) => {
  const taxYears: any[] = [];
  // Include current year (partial) through stopAtYear
  for (let year = startYear + 1; year >= stopAtYear; year--) {
    taxYears.push({
      taxYear: `${year - 1}/${String(year).slice(-2)}`,
      value: year - 1,
    });
  }
  return taxYears;
};

export const TAX_YEARS = getTaxYears();

/**
 * The two US filing details that changed with the 2025 form revisions, in one
 * place so the 8949 screens, the Schedule D helper, the 1040 helper and the
 * 8949 PDF cannot drift apart:
 *  - the digital-asset boxes I/L replace C/F for transactions not reported on
 *    Form 1099-DA or 1099-B;
 *  - Form 1040's capital gain line 7 splits into 7a/7b.
 */
export const getUsFormYearRules = (taxYear: number) => ({
  shortTermBox: taxYear >= 2025 ? "I" : "C",
  longTermBox: taxYear >= 2025 ? "L" : "F",
  capitalGainLine: taxYear >= 2025 ? "7a" : "7",
});

/**
 * Schedule D's carry-in lines (6 and 14) told the taxpayer to supply the figure
 * by hand. The engine now carries the crypto side, so the copy has to say which
 * half is done and which half is still theirs — losses from disposals Moonscape
 * never saw are still their own line to add.
 *
 * `source: "legacy"` means the year was calculated before the engine owned
 * these figures, so no carryover is known and the old instruction stands.
 *
 * Twinned in `server/src/services/reports/pdf/taxConfig.ts` — the print screens
 * and the client PDFs must word the same tax position identically.
 */
export const usCarryinNote = (
  term: "short" | "long",
  amount: number,
  source: "stored" | "legacy"
) => {
  const label = `${term}-term`;
  const worksheetLine = term === "short" ? "8" : "13";
  const worksheet = `line ${worksheetLine} of your Capital Loss Carryover Worksheet`;

  if (source === "legacy")
    return `Enter the amount, if any, from ${worksheet}. Recalculate your tax report to have Moonscape carry your ${label} crypto loss carryover here.`;

  return `${
    amount > 0
      ? `Your ${label} crypto loss carryover is entered here, shown in parentheses, as the form prints it.`
      : `No ${label} crypto loss carries into this year.`
  } Add any ${label} carryover from your other disposals — ${worksheet} — before filing.`;
};

/**
 * The applied-limit sentence on Schedule D line 21 and Form 1040's capital-gain
 * line. `money` is the caller's formatter (Intl on the screens, `irsMoney` in
 * the PDFs). Leading space — the caller appends it to the line's own sublabel.
 *
 * On `source: "legacy"` the year predates the engine owning these figures, so
 * filing status is unknown: state the assumption instead of asserting a limit.
 * Saying "the limit applied to your return is $3,000" to a married-filing-
 * separately taxpayer is simply false, and their real limit is $1,500.
 *
 * Twinned in `server/src/services/reports/pdf/taxConfig.ts`.
 */
export const usLossLimitNote = (
  annual: { lossLimit: number; source: "stored" | "legacy" },
  money: (value: number) => string
) => {
  if (annual.source === "legacy")
    return " The deduction shown assumes a limit of $3,000; if you are married filing separately your limit is $1,500.";

  return ` The limit applied to your return is ${money(annual.lossLimit)}.`;
};

/**
 * The sentence that follows Schedule D line 21 and Form 1040's capital-gain
 * line when a loss runs past the §1211(b) limit. `money` is the caller's
 * formatter (Intl on the screens, `irsMoney` in the PDFs). Empty string when
 * nothing carries — the caller appends it to the line's own sublabel.
 */
export const usCarryforwardNote = (
  annual: {
    carryoverShort: number;
    carryoverLong: number;
    source: "stored" | "legacy";
  },
  money: (value: number) => string
) => {
  if (annual.source === "legacy")
    return " Any loss above the limit carries forward to future tax years; recalculate your tax report to see the short-term and long-term split.";

  const parts = [
    annual.carryoverShort > 0 && `${money(annual.carryoverShort)} short-term`,
    annual.carryoverLong > 0 && `${money(annual.carryoverLong)} long-term`,
  ].filter(Boolean) as string[];

  if (!parts.length) return "";

  return ` The remaining loss is not lost — ${parts.join(" and ")} ${
    parts.length > 1 ? "carry" : "carries"
  } forward to future tax years, keeping its character.`;
};

/**
 * One US-taxpayer test for every filing surface. The server matches with
 * includes() on a lowercased jurisdiction, so "United States of America"
 * passes there; the screens used === "United States" and locked those users
 * out of forms the API was happy to serve.
 *
 * The short forms match whole words only — a plain includes("us") also claims
 * Australia, Belarus and Russia.
 */
const US_PHRASES = ["united states", "america"];
const US_WORDS = ["us", "usa"];

const normalizeJurisdiction = (value?: string | null): string =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const isUsJurisdiction = (value?: string | null): boolean => {
  const normalized = normalizeJurisdiction(value);
  if (!normalized) return false;
  if (US_PHRASES.some((phrase) => normalized.includes(phrase))) return true;
  return normalized.split(" ").some((word) => US_WORDS.includes(word));
};

/**
 * The UK twin, matched the same way and for the same reason: the server tests
 * the jurisdiction with a lowercased includes("united kingdom"), and a screen
 * that insists on an exact string disagrees with the API about who the user is.
 *
 * "Britain" is deliberately absent — no seeded jurisdiction uses it, and it
 * would also claim nothing else, so it is a guess with no case to serve. The
 * short forms match whole words only, as above.
 */
const UK_PHRASES = ["united kingdom", "great britain"];
const UK_WORDS = ["uk", "gb", "gbr"];

export const isUkJurisdiction = (value?: string | null): boolean => {
  const normalized = normalizeJurisdiction(value);
  if (!normalized) return false;
  if (UK_PHRASES.some((phrase) => normalized.includes(phrase))) return true;
  return normalized.split(" ").some((word) => UK_WORDS.includes(word));
};

/**
 * Which tax year a date falls in, named for the year it STARTS in — 2026 means
 * 2026/27. The UK year runs 6 April to 5 April, so January to 5 April belongs
 * to the year before, and a screen asking the API for `getFullYear()` requests
 * the wrong year's report for three months of every twelve.
 *
 * Client-side only because the year has to be known before any request is made.
 * Figures are still the server's: it stamps the year it actually used onto the
 * payload it returns.
 */
export const taxYearOfDate = (
  jurisdiction?: string | null,
  date: Date = new Date()
): number => {
  const year = date.getUTCFullYear();
  if (!isUkJurisdiction(jurisdiction)) return year;
  const month = date.getUTCMonth();
  const beforeApril6 = month < 3 || (month === 3 && date.getUTCDate() < 6);
  return beforeApril6 ? year - 1 : year;
};
